//hooks/useSubscribe.jsx


import { useState, useEffect } from "react";
import axios from "axios";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { loadStripe } from "@stripe/stripe-js";

const API_URL = process.env.REACT_APP_API_URL;
// const API_URL = "http://localhost:9000"; 
// const STRIPE_PUBLIC_KEY = "pk_test_51S0iOxF9sKc1ko7nl8kfs6cG2BJXo1EhC5U9pvOhJADzBg8Qd5JaEPYIVTgPJl9mq3rUr1dpQavRCZFM4e24bsRu00lhKowmsm";
// grab stripe public key from .env
const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

const useSubscribe = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        console.log("[AUTH] User authenticated:", currentUser.uid);
        setUser(currentUser);
      } else {
        console.error("[AUTH ERROR] User not authenticated.");
        setError("User not authenticated.");
      }
    });

    return () => unsubscribe();
  }, [auth]);

  // ------------------ EXISTING: agents ------------------
  const startSubscription = async (billingCycle) => {
    if (!user?.uid) {
      setError("User authentication is missing.");
      return;
    }
    if (!billingCycle || (billingCycle !== "monthly" && billingCycle !== "yearly")) {
      setError("Invalid billing cycle. Choose 'monthly' or 'yearly'.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/api/subscribe`, {
        uid: user.uid,
        billingCycle,
      });

      const { sessionId } = response.data;
      if (!sessionId) throw new Error("No session ID returned from API.");

      if (!STRIPE_PUBLIC_KEY) {
        throw new Error("Stripe public key is not configured. Please set REACT_APP_STRIPE_PUBLIC_KEY or REACT_APP_STRIPE_PUBLISHABLE_KEY in your .env file.");
      }

      const stripe = await loadStripe(STRIPE_PUBLIC_KEY);
      if (!stripe) throw new Error("Stripe.js failed to load!");

      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) setError(error.message);
    } catch (err) {
      console.error("[API ERROR] Subscription Error:", err);
      setError(err.response?.data?.error || "Subscription failed.");
    } finally {
      setLoading(false);
    }
  };

  // ------------------ NEW: owners/landlords/managers ------------------
  /**
   * plan: "owner_basic" | "owner_warranty"
   * billingCycle is locked to "monthly" in the backend for this userType.
   */
  const startOwnerSubscription = async (plan) => {
    if (!user?.uid) {
      setError("User authentication is missing.");
      return;
    }
    if (plan !== "owner_basic" && plan !== "owner_warranty") {
      setError("Invalid owner plan. Choose 'owner_basic' or 'owner_warranty'.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API_URL}/api/subscribe/owner`, {
      // const response = await axios.post(`${API_URL}/api/subscribe/owner`, {
        uid: user.uid,
        plan,
        billingCycle: "monthly",
      });

      const { sessionId } = response.data;
      if (!sessionId) throw new Error("No session ID returned from API.");

      if (!STRIPE_PUBLIC_KEY) {
        throw new Error("Stripe public key is not configured. Please set REACT_APP_STRIPE_PUBLIC_KEY or REACT_APP_STRIPE_PUBLISHABLE_KEY in your .env file.");
      }

      const stripe = await loadStripe(STRIPE_PUBLIC_KEY);
      if (!stripe) throw new Error("Stripe.js failed to load!");

      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) setError(error.message);
    } catch (err) {
      console.error("[API ERROR] Owner Subscription Error:", err);
      setError(err.response?.data?.error || "Subscription failed.");
    } finally {
      setLoading(false);
    }
  };

  return { startSubscription, startOwnerSubscription, loading, error };
};

export default useSubscribe;
